'use client';
import { useTranslations } from 'next-intl';
import { memo, useEffect, useRef } from 'react';
import { BREAKPOINTS } from '@/app/constants';
import { cn } from '@/app/utils';
import { playMediaSafely } from '@/app/utils/client';
import type { VideoResource } from './constants';

interface SequentialVideoCarouselProps {
  resources: VideoResource[];
  currentIndex: number;
  enabled: boolean;
  onEnded: () => void;
}

/**
 * 顺序播放轮播：所有视频常驻 absolute DOM，不挂载/卸载，仅 opacity 切换。
 * 当前 video 自然播完触发 onEnded，由父组件推进 currentIndex。
 */
export const SequentialVideoCarousel = memo(
  ({ resources, currentIndex, enabled, onEnded }: SequentialVideoCarouselProps) => {
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const t = useTranslations();

    useEffect(() => {
      if (!enabled) return;
      videoRefs.current.forEach((video, index) => {
        if (!video) return;
        if (index === currentIndex) {
          video.currentTime = 0;
          playMediaSafely(video);
        } else {
          video.pause();
        }
      });
    }, [currentIndex, enabled]);

    return (
      <>
        {resources.map((resource, index) => {
          const altText = t(`home3.heroSection.prompts.${resource.name}`);
          const isActive = index === currentIndex;
          const mobileSourceMedia = `(max-width: ${BREAKPOINTS.SM_MAX}px)`;
          const desktopSourceMedia = resource.mobileVideoSrc ? `(min-width: ${BREAKPOINTS.MD_MIN}px)` : undefined;
          return (
            <div
              key={`hero-seq-${resource.url}`}
              className={cn(
                '-z-20 absolute top-0 left-0 h-full w-full transition-opacity duration-150 ease-in-out',
                isActive ? 'opacity-100' : 'opacity-0',
              )}
            >
              <picture>
                {resource.mobilePoster ? <source media={mobileSourceMedia} srcSet={resource.mobilePoster} /> : null}
                <img
                  src={resource.poster}
                  alt={altText}
                  decoding="async"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  fetchPriority={index === 0 ? 'high' : 'auto'}
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />
              </picture>
              {enabled && (
                <video
                  ref={(el) => {
                    videoRefs.current[index] = el;
                  }}
                  className="absolute inset-0 h-full w-full object-cover object-center"
                  // 所有 video 都在 parse 期带 autoplay：MIUI / vivo U4/T7 / TBS X5 根据
                  // parse 期属性把 <video> 分档——有 autoplay 的走"浏览器自动 inline 播放"
                  // 档，之后 JS .play()/.pause() 都在 WebView 内；没有 autoplay 的走"用户
                  // 主动播放"档，从 useEffect 里调 .play() 会被判成"用户想看这条视频"→
                  // 拉起原生播放器浮层。轮播 2+ 都走 useEffect→.play()，必须归入自动 inline
                  // 档。non-active 的 preload="none" 保证 readyState=0，autoplay 触发条件
                  // （数据就绪 AND paused=false）不满足，不会真的解码。
                  autoPlay
                  muted
                  playsInline
                  preload={isActive ? 'auto' : 'none'}
                  // 国产 Android 内核在 HTML parse 期就锁定分档，inline 属性必须写在
                  // JSX 里让 React 首屏 render 就写进 HTML。老取值 x5-video-player-type=
                  // "h5" 在 TBS 9+ / 新版 MIUI 已按普通视频劫持，必须用 "h5-page"。
                  // T7/U4（vivo 自带浏览器、UC 系）不认 x5-*，独立走 t7-video-player-type。
                  webkit-playsinline="true"
                  x5-playsinline="true"
                  x5-video-player-type="h5-page"
                  x5-video-player-fullscreen="false"
                  t7-video-player-type="inline"
                  // 禁掉国产内核在视频上强制叠的原生控件里 Chromium 系认的部分：画中画、
                  // 投屏、下载、全屏、倍速按钮。vivo 的"登陆vivo账号"、"更多"等属于
                  // WebView 之外的原生 UI，标准 API 无法关闭。
                  disablePictureInPicture
                  disableRemotePlayback
                  controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                  onEnded={isActive ? onEnded : undefined}
                >
                  {resource.mobileVideoSrc ? (
                    <source media={mobileSourceMedia} src={resource.mobileVideoSrc} type="video/mp4" />
                  ) : null}
                  <source media={desktopSourceMedia} src={resource.url} type="video/mp4" />
                </video>
              )}
            </div>
          );
        })}
      </>
    );
  },
);

SequentialVideoCarousel.displayName = 'SequentialVideoCarousel';
